import React, { SyntheticEvent } from "react";
import {
  Autocomplete,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from "@mui/material";
import { useFilters } from "src/hooks/useFilters";
import { FilterType } from "src/api/types";
import { isTruthy } from "src/utils/isTruthy";

interface Props<T> {
  current: T;
}

function Filter<T extends FilterType>({ current }: Props<T>) {
  const [filter, setFilter] = useFilters(current.name);

  const handleChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setFilter(event.target.value);
    },
    [setFilter],
  );

  const handleCheckbox = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setFilter(String(event.target.checked));
    },
    [setFilter],
  );

  const handleAutoComplete = React.useCallback(
    (_: SyntheticEvent, value: string | null) => {
      setFilter(value ?? "");
    },
    [setFilter],
  );

  const handleMultiSelect = React.useCallback(
    (e: SelectChangeEvent<string[]>) => {
      const {
        target: { value },
      } = e;

      const newValue = typeof value === "string" ? value.split(",") : value;

      setFilter(newValue.join(","));
    },
    [setFilter],
  );

  if (current.type === "multiselect") {
    const value = filter.split(",").filter(isTruthy);

    return (
      <FormControl sx={{ width: "180px" }} size="small">
        <InputLabel>{current.placeholder}</InputLabel>
        <Select
          value={value}
          multiple
          onChange={handleMultiSelect}
          label={current.placeholder}
        >
          {current.values.map((item) => (
            <MenuItem key={item} value={item}>
              {item}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  }

  if (current.type === "custom") {
    return (
      <current.component
        filter={filter}
        onChange={handleChange}
        placeholder={current.placeholder}
      />
    );
  }

  if (current.type === "select") {
    const items =
      "objectValues" in current
        ? current.objectValues.map((value) => (
            <MenuItem key={value.name} value={value.value}>
              {value.name}
            </MenuItem>
          ))
        : current.values.map((value) => (
            <MenuItem key={value} value={value}>
              {value}
            </MenuItem>
          ));

    return (
      <TextField
        label={current.placeholder}
        select
        sx={{ width: 180 }}
        size="small"
        value={filter}
        onChange={handleChange}
      >
        <MenuItem value={""}>{"None"}</MenuItem>
        {items}
      </TextField>
    );
  }

  if (current.type === "autocomplete") {
    return (
      <Autocomplete
        onChange={handleAutoComplete}
        sx={{ width: 180 }}
        size="small"
        disablePortal
        value={filter}
        options={current.values}
        renderInput={(params) => (
          <TextField {...params} label={current.placeholder} />
        )}
      />
    );
  }

  if (current.type === "checkbox") {
    return (
      <FormControlLabel
        checked={filter === "true"}
        control={<Checkbox onChange={handleCheckbox} />}
        label={current.label}
      />
    );
  }

  return (
    <TextField
      size="small"
      label={current.placeholder}
      value={filter}
      onChange={handleChange}
    />
  );
}

export default Filter;
